import { useEmpresasProjetaveis } from "../../lib/projecao/hooks";
import { useTemProjecaoIrrestrita } from "../../lib/auth/permissoes";
import { ErroHttp } from "../../lib/http/erros";

/*
 * Seletor de escopo da projeção (FE-2 CP-B — T1, peça de 1ª classe).
 *
 * Consome `useEmpresasProjetaveis()` — a lista JÁ vem filtrada pelo escopo do
 * usuário no backend (FE-B3 GET /api/projecao/empresas). A UI NÃO recebe nem
 * renderiza nada fora do escopo: não há filtragem client-side de empresa, só a
 * apresentação do que o servidor autorizou.
 *
 * Rótulo de cada empresa: `fantasia ?? razao_social` (nem toda empresa cadastra
 * nome fantasia).
 *
 * GRUPO (T4): a opção "Grupo (consolidado)" só aparece se
 * `useTemProjecaoIrrestrita()` for true. Usuário com escopo limitado NUNCA vê a
 * opção grupo — espelha o RBAC discovery (`irrestrito`).
 *
 * ESCOPO VAZIO (REFORÇO #1): quando a lista vem `[]` (200 vazio do FE-B3, usuário
 * sem empresas projetáveis) E o usuário NÃO é irrestrito (sem grupo), renderiza
 * um estado CLARO em vez de um seletor vazio confuso. Se irrestrito mas lista
 * vazia, a opção Grupo ainda existe e o seletor é renderizado.
 */

/**
 * O escopo selecionado, modelado como união discriminada. `empresa` carrega o id
 * da empresa; `grupo` é o consolidado (só para usuário irrestrito).
 */
export type EscopoSelecionado =
  | { tipo: "empresa"; empresaId: string }
  | { tipo: "grupo" };

/** Valor do `<select>` para o escopo de grupo (sentinela fora do espaço de ids). */
const VALOR_GRUPO = "grupo";

interface SeletorEscopoProps {
  /** Escopo atualmente selecionado, ou `null` enquanto nada foi escolhido. */
  selecionado: EscopoSelecionado | null;
  /** Notifica a página da nova seleção. */
  aoSelecionar: (escopo: EscopoSelecionado) => void;
}

export function SeletorEscopo({
  selecionado,
  aoSelecionar,
}: SeletorEscopoProps): JSX.Element {
  const irrestrito = useTemProjecaoIrrestrita();
  const consulta = useEmpresasProjetaveis();

  if (consulta.isPending) {
    return (
      <p className="font-corpo text-base text-off-black">
        Carregando empresas…
      </p>
    );
  }

  if (consulta.isError) {
    const mensagem =
      consulta.error instanceof ErroHttp
        ? consulta.error.message
        : "Não foi possível carregar as empresas.";
    return (
      <p role="alert" className="font-corpo text-base text-flame">
        {mensagem}
      </p>
    );
  }

  const empresas = consulta.data;

  // ESCOPO VAZIO sem grupo: nada a selecionar — estado claro, não seletor vazio.
  if (empresas.length === 0 && !irrestrito) {
    return (
      <p className="font-corpo text-base text-off-black">
        Você não tem empresas para projetar.
      </p>
    );
  }

  const valorAtual =
    selecionado === null
      ? ""
      : selecionado.tipo === "grupo"
        ? VALOR_GRUPO
        : selecionado.empresaId;

  function aoMudar(evento: React.ChangeEvent<HTMLSelectElement>): void {
    const valor = evento.target.value;
    if (valor === "") {
      return;
    }
    if (valor === VALOR_GRUPO) {
      aoSelecionar({ tipo: "grupo" });
      return;
    }
    aoSelecionar({ tipo: "empresa", empresaId: valor });
  }

  return (
    <label className="flex flex-col gap-1 font-corpo text-base text-off-black">
      <span className="font-subtitulo">Escopo da projeção</span>
      <select
        value={valorAtual}
        onChange={aoMudar}
        className="rounded-md border border-platinum bg-snow px-2 py-1 text-off-black"
      >
        <option value="" disabled>
          Selecione…
        </option>
        {irrestrito && <option value={VALOR_GRUPO}>Grupo (consolidado)</option>}
        {empresas.map((empresa) => (
          <option key={empresa.id} value={empresa.id}>
            {empresa.fantasia ?? empresa.razao_social}
          </option>
        ))}
      </select>
    </label>
  );
}
